import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Buildings, Users, Rocket, Crown, ArrowRight, SpinnerGap } from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

const PLAN_DETAILS = {
  starter: {
    icon: Rocket,
    color: "#3b82f6",
    popular: false,
    features: ["Chat IA avec citations video", "Quiz auto-générés", "Base de connaissances", "Parcours d'onboarding", "Bilingue FR/EN", "10 utilisateurs max", "15 videos/mois"],
  },
  business: {
    icon: Buildings,
    color: "#8b5cf6",
    popular: true,
    features: ["Tout Starter +", "Analytics avancees", "Coach IA personnel", "Génération video avatar IA", "Gamification & classement", "Alertes intelligentes", "50 utilisateurs max", "50 videos/mois"],
  },
  enterprise: {
    icon: Crown,
    color: "#f59e0b",
    popular: false,
    features: ["Tout Business +", "Acces API", "Support prioritaire", "Branding personnalise", "200 utilisateurs max", "Videos illimitees"],
  },
};

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState("monthly");
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("plans");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [error, setError] = useState("");

  useState(() => {
    axios.get(`${API}/api/plans`).then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setStep("register");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/organizations/register`, {
        name: formData.name,
        plan: selectedPlan,
        billing_cycle: billing,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
      }, { withCredentials: true });

      // Create Stripe checkout
      const { data: checkout } = await axios.post(`${API}/api/organizations/checkout`, {
        plan: selectedPlan,
        billing_cycle: billing,
        origin_url: window.location.origin,
      }, { withCredentials: true });

      if (checkout.url) {
        window.location.href = checkout.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'inscription");
    }
    setLoading(false);
  };

  const planData = plans || [
    { id: "starter", name: "Starter", price_monthly: 249, price_annual: 2490, max_users: 10 },
    { id: "business", name: "Business", price_monthly: 599, price_annual: 5990, max_users: 50 },
    { id: "enterprise", name: "Enterprise", price_monthly: 1199, price_annual: 11990, max_users: 200 },
  ];

  if (step === "register") {
    const plan = planData.find((p) => p.id === selectedPlan);
    const details = PLAN_DETAILS[selectedPlan] || {};
    const price = billing === "annual" ? plan?.price_annual : plan?.price_monthly;
    const Icon = details.icon || Buildings;

    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 py-12" data-testid="org-register-page">
        <div className="w-full max-w-lg">
          <button onClick={() => setStep("plans")} className="text-sm text-zinc-400 hover:text-white mb-6 flex items-center gap-1">
            ← Retour aux plans
          </button>

          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${details.color}20` }}>
                <Icon size={22} style={{ color: details.color }} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white font-['Outfit']">Plan {plan?.name}</h2>
                <p className="text-sm text-zinc-400">
                  {billing === "annual" ? `${(price / 12).toFixed(0)}$/mois (facture annuellement)` : `${price}$/mois`}
                </p>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="text-sm text-zinc-300 block mb-1">Nom de l'entreprise</label>
                <input
                  type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Cabinet Finance XYZ"
                  data-testid="org-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 block mb-1">Votre nom complet</label>
                <input
                  type="text" required value={formData.admin_name}
                  onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Jean Tremblay"
                  data-testid="admin-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 block mb-1">Email administrateur</label>
                <input
                  type="email" required value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="jean@cabinetxyz.com"
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-300 block mb-1">Mot de passe</label>
                <input
                  type="password" required value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Min. 8 caracteres"
                  minLength={8}
                  data-testid="admin-password-input"
                />
              </div>

              {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}

              <button
                type="submit" disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                {loading ? <SpinnerGap size={20} className="animate-spin" /> : <ArrowRight size={20} />}
                {loading ? "Creation en cours..." : "Continuer vers le paiement"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-16 px-4" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="text-sm text-blue-400 font-semibold tracking-wider uppercase mb-3">Tarification</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Outfit'] mb-4">
          Choisissez votre plan
        </h1>
        <p className="text-zinc-400 text-lg">
          3x moins cher que d'acheter un LMS + Knowledge Base + Video Avatar separement
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => setBilling("monthly")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billing === "monthly" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}
            data-testid="billing-monthly"
          >
            Mensuel
          </button>
          <button
            onClick={() => setBilling("annual")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${billing === "annual" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}
            data-testid="billing-annual"
          >
            Annuel <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded-full">2 mois gratuits</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        {planData.map((plan) => {
          const details = PLAN_DETAILS[plan.id] || {};
          const Icon = details.icon || Buildings;
          const price = billing === "annual" ? plan.price_annual : plan.price_monthly;
          const monthlyEquiv = billing === "annual" ? (plan.price_annual / 12).toFixed(0) : plan.price_monthly;

          return (
            <div
              key={plan.id}
              className={`relative bg-zinc-900 border rounded-2xl p-6 flex flex-col transition-all hover:border-blue-500/50 hover:-translate-y-1 ${
                details.popular ? "border-blue-500 shadow-lg shadow-blue-500/10" : "border-zinc-800"
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {details.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                  POPULAIRE
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${details.color}20` }}>
                  <Icon size={22} style={{ color: details.color }} weight="duotone" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white font-['Outfit']">{plan.name}</h3>
                  <div className="flex items-center gap-1">
                    <Users size={12} className="text-zinc-500" />
                    <span className="text-xs text-zinc-500">{plan.max_users} utilisateurs</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-bold text-white font-['Outfit']">${monthlyEquiv}</span>
                  <span className="text-zinc-500 text-sm mb-1">/mois</span>
                </div>
                {billing === "annual" && (
                  <p className="text-xs text-teal-400 mt-1">Facture {price}$/an (economisez {plan.price_monthly * 2}$)</p>
                )}
              </div>

              <ul className="space-y-2.5 mb-8 flex-1">
                {(details.features || []).map((feat, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check size={16} className="text-teal-400 flex-shrink-0 mt-0.5" weight="bold" />
                    {feat}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                className={`w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                  details.popular
                    ? "bg-blue-600 hover:bg-blue-500 text-white"
                    : "bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                }`}
                data-testid={`select-plan-${plan.id}`}
              >
                Commencer
                <ArrowRight size={16} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer CTA */}
      <div className="text-center mt-12">
        <p className="text-zinc-500 text-sm">
          Besoin de plus de 200 utilisateurs ?{" "}
          <a href="mailto:contact@knoria.ai" className="text-blue-400 hover:text-blue-300 underline">Contactez-nous</a>
        </p>
      </div>
    </div>
  );
}
